import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { appPaths, HOME_PATH } from "../../shared/routing/appPaths";
import { useSession } from "../../session/sessionContext";
import AppButton from "../../ui/buttons/AppButton";
import GuideLink from "../guide/GuideLink";
import AuthPageSwitcher from "./AuthPageSwitcher";
import LanguageThemeControls from "../../ui/controls/LanguageThemeControls";
import { navigateWithPageTransition } from "./pageTransition";
import Chip from "../../ui/feedback/Chip";

const PAGE_CONTAINER_CLASS = "app-page-container";
const CARD_SECTION_CLASS = "app-card app-card-section";

export default function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session } = useSession();

  useEffect(() => {
    if (session.status === "signedIn") {
      void navigate(HOME_PATH);
    }
  }, [session.status, navigate]);

  const workflowSteps = [
    { number: "1", titleKey: "landing.workflow.steps.0.title" },
    { number: "2", titleKey: "landing.workflow.steps.1.title" },
    { number: "3", titleKey: "landing.workflow.steps.2.title" },
  ];

  const features = [
    { titleKey: "landing.features.items.0.title", descKey: "landing.features.items.0.desc" },
    { titleKey: "landing.features.items.1.title", descKey: "landing.features.items.1.desc" },
    { titleKey: "landing.features.items.2.title", descKey: "landing.features.items.2.desc" },
    { titleKey: "landing.features.items.3.title", descKey: "landing.features.items.3.desc" },
  ];

  const navigateToLogin = () => {
    navigateWithPageTransition((to) => void navigate(to), appPaths.login(), "right");
  };

  return (
    <div className="app-shell min-h-screen">
      <header className="app-surface app-divider-top sticky top-0 z-50 border-b backdrop-blur-sm">
        <div className={`${PAGE_CONTAINER_CLASS} flex flex-wrap items-start justify-between gap-3 py-4`}>
          <div>
            <h1 className="app-title text-2xl font-bold">{t("landing.header.title")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <GuideLink asButton />
            <AuthPageSwitcher to={appPaths.login()} direction="right" label={t("app.toLogin")} compact={true} />
            <LanguageThemeControls
              className="w-auto"
              compact={true}
              subtle={true}
              showLabels={false}
              showSymbols={true}
              slim={true}
              layout="inline"
            />
          </div>
        </div>
      </header>

      <section className={`${PAGE_CONTAINER_CLASS} py-16`}>
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <h2 className="app-title mb-4 text-4xl font-bold leading-tight md:text-5xl">
              {t("landing.hero.title")}
            </h2>
            <div className="flex flex-wrap gap-3">
              <AppButton onClick={navigateToLogin} variant="primary">
                {t("landing.hero.start")}
              </AppButton>
              <AppButton
                onClick={() => document.getElementById("workflow")?.scrollIntoView({ behavior: "smooth" })}
                variant="secondary"
              >
                {t("landing.hero.more")}
              </AppButton>
            </div>
          </div>
          <div className="app-card p-6">
            <div className="space-y-3">
              <p className="app-text text-sm font-semibold">{t("landing.heroCard.doc")}</p>
              <div className="app-card-muted p-3">
                <p className="app-title text-sm font-semibold">{t("landing.heroCard.annotatedTitle")}</p>
                <p className="app-text-muted mt-1 text-xs">{t("landing.heroCard.annotatedDesc")}</p>
              </div>
              <div className="app-card-muted p-3">
                <p className="app-title text-sm font-semibold">{t("landing.heroCard.modelTitle")}</p>
                <p className="app-text-muted mt-1 text-xs">{t("landing.heroCard.modelDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="app-surface-muted app-divider-top py-14">
        <div className={PAGE_CONTAINER_CLASS}>
          <div className="grid gap-5 md:grid-cols-3">
            {workflowSteps.map((step) => (
              <div key={step.number} className={CARD_SECTION_CLASS}>
                <div className="mb-3 flex items-center gap-3">
                  <Chip className="font-bold">{step.number}</Chip>
                  <h4 className="app-title text-base font-semibold">{t(step.titleKey)}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${PAGE_CONTAINER_CLASS} py-14`}>
        <div className="grid gap-5 md:grid-cols-2">
          {features.map((feature) => (
            <div key={feature.titleKey} className={CARD_SECTION_CLASS}>
              <h4 className="app-title mb-2 text-lg font-semibold">{t(feature.titleKey)}</h4>
              <p className="app-text-muted text-sm">{t(feature.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={`${PAGE_CONTAINER_CLASS} pb-12`}>
        <div className="app-card p-7 text-center">
          <AppButton onClick={navigateToLogin} variant="primary">
            {t("landing.cta.action")}
          </AppButton>
        </div>
      </section>

    </div>
  );
}
